import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter, map, shareReplay} from 'rxjs/operators';

export interface Config {
  partnerId: string;
  marketId: string;
  accountId: string;
  groupPath: string;
  serviceProviderId: string;
  defaultURL: string;
}

export enum SidenavType {
  NOT_SET = 0,
  ACCOUNT_GROUP = 1,
  BRAND = 2,
}

@Injectable({providedIn: 'root'})
export class BusinessNavConfigService {
  private config$$: BehaviorSubject<Config> = new BehaviorSubject<Config>(null);
  public config$: Observable<Config>;
  public navType$: Observable<SidenavType>;
  public isBrandType$: Observable<boolean>;
  public isAccountGroupType$: Observable<boolean>;
  public partnerId$: Observable<string>;
  public marketId$: Observable<string>;
  public accountGroupId$: Observable<string>;
  public groupPath$: Observable<string>;
  public serviceProviderId$: Observable<string>;
  public locationId$: Observable<string>;

  get locationId(): string {
    const cfg = this.config$$.getValue();
    if (!cfg) {
      return '';
    }
    return (!!cfg.accountId) ? cfg.accountId : cfg.groupPath;
  }

  constructor() {
    this.config$ = this.config$$.asObservable().pipe(
      filter((config: Config) => !!config && (!!config.accountId || !!config.groupPath))
    );
    this.navType$ = this.config$.pipe(
      map((config: Config) => {
        if (!!config.accountId) {
          return SidenavType.ACCOUNT_GROUP;
        } else if (!!config.groupPath) {
          return SidenavType.BRAND;
        }
        return SidenavType.NOT_SET;
      }),
      shareReplay(1)
    );
    this.isBrandType$ = this.navType$.pipe(
      map((type: SidenavType) => type === SidenavType.BRAND),
      shareReplay(1),
    );
    this.isAccountGroupType$ = this.navType$.pipe(
      map((type: SidenavType) => type === SidenavType.ACCOUNT_GROUP),
      shareReplay(1),
    );
    this.partnerId$ = this.config$.pipe(
      map((config: Config) => config.partnerId),
      shareReplay(1),
    );
    this.marketId$ = this.config$.pipe(
      map((config: Config) => config.marketId),
      shareReplay(1),
    );
    this.accountGroupId$ = this.config$.pipe(
      map((config: Config) => config.accountId),
      shareReplay(1),
    );
    this.groupPath$ = this.config$.pipe(
      map((config: Config) => config.groupPath),
      shareReplay(1),
    );
    this.serviceProviderId$ = this.config$.pipe(
      map((config: Config) => config.serviceProviderId),
      shareReplay(1),
    );
    this.locationId$ = this.config$.pipe(
      map((config: Config) => (!!config.accountId) ? config.accountId : config.groupPath),
      shareReplay(1),
    );
  }

  public getCurrentConfig(): Config {
    return this.config$$.getValue();
  }

  public initialize(partnerId: string, marketId: string, accountId: string, groupPath: string, serviceProviderId: string, userId: string, defaultURL?: string): void {
    this.config$$.next({
      partnerId: partnerId,
      marketId: marketId,
      accountId: accountId,
      groupPath: groupPath,
      serviceProviderId: serviceProviderId,
      defaultURL: defaultURL
    } as Config);
  }
}
